import React, { useMemo } from 'react';
import PreviewScreen from './PreviewScreen';
import { EmptyState } from '../../../../components/commons';
import { InvalidSearchImg } from '../../../../images/images';
import Button from '../../../../components/ComponentsV1/Button';
import { Info, Link } from 'lucide-react';
import Shimmer from 'sushiweb/Shimmer';
import InputV1 from '../../../../components/ComponentsV1/InputV1';
import toast from 'react-hot-toast';
import { Checkbox } from './Elements/Checkbox';
import { ENTITY_TYPE, MAX_TAT, MIN_TAT, SLUGS } from './constants';
import ParentCategoryDetailsSheet from './Elements/ParentCategoryDetailsSheet';

const getVal = (data, slug) => data?.find((f) => f?.slug === slug)?.value;

const renderFormFields = (fieldArray = [], isTaskClosed, handleChange) => {
  return fieldArray.map((field) => {
    if (!field) {
      return null;
    }

    const {
      title = '',
      data_type = 'text',
      value = '',
      slug = '',
      options = [],
      placeholder = '',
    } = field;

    const normalizedOptions =
      slug === 'return_policy_image'
        ? options?.map((option) => ({
            label: option?.title,
            value: option?.url,
          }))
        : data_type === 'boolean'
        ? [
            {
              value: 'true',
              label: 'True',
            },
            {
              value: 'false',
              label: 'False',
            },
          ]
        : options?.map((option) => ({
            label: option,
            value: option,
          }));

    return (
      <div
        className='tw-w-full tw-flex tw-flex-col tw-items-start tw-gap-[0.6rem]'
        key={slug}
      >
        <span className='tw-font-medium tw-text-zinc-700'>{title}</span>
        {data_type === 'image' ? (
          <div className='tw-p-4 tw-border tw-border-solid tw-border-zinc-300 tw-rounded-lg tw-w-full tw-flex tw-gap-4'>
            <div className='tw-w-[30rem] tw-h-[10rem] tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-bg-[#f8f9fc]'>
              {!value ? (
                <div className='tw-font-medium tw-text-zinc-500'>No Image</div>
              ) : (
                <img
                  src={value}
                  alt=''
                  className='tw-w-full tw-h-full tw-rounded-lg'
                />
              )}
            </div>
            <div className='tw-w-full'>
              <InputV1
                searchIcon={
                  <Link
                    size={18}
                    strokeWidth={2.4}
                    className='tw-text-base-green'
                  />
                }
                disabled={isTaskClosed}
                optionHeight={120}
                options={normalizedOptions}
                className='tw-w-full'
                placeholder={placeholder}
                multiple={false}
                renderOption={(opt) => {
                  return (
                    <div className='tw-w-full tw-h-[10rem] tw-flex tw-items-center tw-justify-start tw-overflow-hidden tw-rounded-lg'>
                      <div className='tw-w-[16rem] tw-h-[10rem] tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg'>
                        <img
                          src={opt.value}
                          alt={opt.label}
                          className='tw-w-full tw-h-full'
                        />
                      </div>
                      <div className='tw-font-medium tw-whitespace-nowrap'>
                        {opt.label}
                      </div>
                    </div>
                  );
                }}
                isSearchable={true}
                inputProps={{
                  className: 'tw-w-[55rem] tw-border-none',
                  type: 'text',
                }}
                closeOnSelect
                onChange={(selectedValue) => handleChange(slug, selectedValue)}
                onSelect={(_, selectedValue) =>
                  handleChange(slug, selectedValue)
                }
                value={value}
              />
            </div>
          </div>
        ) : (
          <InputV1
            disabled={isTaskClosed}
            options={normalizedOptions}
            className='tw-w-full'
            placeholder={placeholder}
            multiple={false}
            isSearchable={normalizedOptions?.length > 0}
            inputProps={{
              className: 'tw-w-full tw-border-none',
              type: data_type,
            }}
            closeOnSelect
            onChange={(selectedValue) => handleChange(slug, selectedValue)}
            onSelect={(_, selectedValue) => handleChange(slug, selectedValue)}
            value={value}
          />
        )}
      </div>
    );
  });
};

const ConfigUpdateForm = ({
  resetForm,
  formData,
  updatedFormData,
  setUpdatedFormData,
  isFormLoading = false,
  parentCategoryDetails = {},
  //
  entityId,
  taskId,
  isTaskClosed = false,
  //
  isPreviewMode,
  previewData = {},
  previewLoading,
  setPreviewMode,
  setPreviewPayload,
  //
  getChangeLogs = () => {},
  isError = false,
  isSubmitting,
  selectedEntityType,
}) => {
  const [showParentCategory, setShowParentCategory] = React.useState(false);

  const slugToNameMap = useMemo(() => {
    return (
      formData?.reduce((acc, field) => {
        acc[field?.slug] = field?.title;
        return acc;
      }, {}) || {}
    );
  }, [formData]);

  // picks the updated value along with the field and set it in the form data
  const handleChange = (field, value) => {
    setUpdatedFormData((prev) =>
      prev.map((item) =>
        item.slug === field ? { ...item, value: value } : item,
      ),
    );
  };

  const handlePreview = () => {
    if (!updatedFormData) return;

    const updatedTatValue = updatedFormData?.find(
      (f) => f.slug === SLUGS.TAT,
    )?.value;

    const numTat = Number(updatedTatValue);

    if (!Number.isInteger(numTat) || numTat < MIN_TAT || numTat > MAX_TAT) {
      toast.error(`TAT must be an integer between ${MIN_TAT} and ${MAX_TAT}.`);
      return;
    }

    const isInAppSupportEnabled =
      getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) === 'true';
    const isExchangeEnabled =
      getVal(updatedFormData, SLUGS.IN_APP_EXCHANGE_ENABLED) === 'true';
    const isReturnEnabled =
      getVal(updatedFormData, SLUGS.IN_APP_RETURN_ENABLED) === 'true';

    if (isInAppSupportEnabled && !isExchangeEnabled && !isReturnEnabled) {
      toast.error(
        'In-App Support requires either Exchange or Return to be enabled.',
      );
      return;
    }

    // Compute the next form data first so logs reflect the actual next state
    let nextUpdatedFormData = updatedFormData;
    if (!isInAppSupportEnabled) {
      // If in_app_support_enabled is false, reset dependent flags to their original values
      nextUpdatedFormData = updatedFormData.map((item) => {
        if (item.slug === SLUGS.IN_APP_EXCHANGE_ENABLED) {
          return { ...item, value: 'false' };
        }
        if (item.slug === SLUGS.IN_APP_RETURN_ENABLED) {
          return { ...item, value: 'false' };
        }
        return item;
      });

      // Apply the computed state once
      setUpdatedFormData(nextUpdatedFormData);
    }

    setPreviewPayload({
      payload: nextUpdatedFormData,
      id: entityId,
      taskId,
      isPreviewData: true,
    });
  };

  const showParentCategoryDetails = () => {
    setShowParentCategory((prev) => !prev);
  };

  const handleSetPLCClick = () => {
    if (!parentCategoryDetails?.length) return;

    setUpdatedFormData((prev) =>
      prev.map((item) => {
        const parentField = parentCategoryDetails.find(
          (f) => f.slug === item.slug,
        );
        return parentField ? { ...item, value: parentField.value } : item;
      }),
    );
  };

  if (isPreviewMode)
    return (
      <PreviewScreen
        changeLog={previewData?.changelog}
        updatedFormData={updatedFormData}
        entityId={entityId}
        taskId={taskId}
        setPreviewPayload={setPreviewPayload}
        resetForm={resetForm}
        isSubmitting={isSubmitting}
        isTaskClosed={isTaskClosed}
        slugToNameMap={slugToNameMap}
        setPreviewMode={setPreviewMode}
        previewLoading={previewLoading}
        isError={isError}
        selectedEntityType={selectedEntityType}
      />
    );

  if (isFormLoading)
    return (
      <div className='tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-overflow-hidden tw-bg-[#e8eaf5]'>
        <div className='tw-h-[7rem]' />
        <Shimmer height='45rem' width='100%' />
      </div>
    );

  if (isError)
    return (
      <div className='tw-pb-6 tw-bg-white tw-rounded-b-lg'>
        <EmptyState img={InvalidSearchImg} heading='Something went wrong' />
      </div>
    );

  if (!formData?.length)
    return (
      <div className='tw-pb-6 tw-bg-white tw-rounded-b-lg'>
        <EmptyState img={InvalidSearchImg} heading='No attributes available' />
      </div>
    );

  return (
    <div className='tw-relative tw-flex tw-flex-col tw-bg-white tw-rounded-b-lg tw-overflow-hidden tw-overflow-x-hidden tw-max-h-[80vh]'>
      {/* Parent Category details sheet */}
      <ParentCategoryDetailsSheet
        showParentCategory={showParentCategory}
        showParentCategoryDetails={showParentCategoryDetails}
        parentCategoryDetails={parentCategoryDetails}
        handleSetPLCClick={handleSetPLCClick}
      />
      {/* Form */}
      <div className='tw-w-full tw-p-8 tw-flex tw-flex-col'>
        {/* TAT and header fields */}
        <div className='tw-flex tw-items-center tw-justify-between tw-gap-6 tw-mb-8'>
          {renderFormFields(
            updatedFormData?.slice(0, 2),
            isTaskClosed,
            handleChange,
          )}
        </div>
        {/* Policy text, image, and in-app enabled fields */}
        <div className='tw-flex tw-flex-col tw-gap-8'>
          {renderFormFields(
            updatedFormData?.slice(2, 5),
            isTaskClosed,
            handleChange,
          )}
        </div>

        {/* last two checkboxes */}
        <div
          className={`tw-flex tw-items-center tw-gap-8 ${
            getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) === 'true'
              ? 'tw-mt-8'
              : ''
          }`}
          style={{
            maxHeight:
              getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) === 'true'
                ? '50rem'
                : '0',
            opacity:
              getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) === 'true'
                ? 1
                : 0,
            transform:
              getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) === 'true'
                ? 'translateY(0)'
                : 'translateY(-1px)',
            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
            overflow: 'hidden',
          }}
        >
          {updatedFormData?.slice(5).map((field) => {
            if (!field) {
              return null;
            }

            return (
              <Checkbox
                key={field.slug}
                isDisable={
                  getVal(updatedFormData, SLUGS.IN_APP_SUPPORT_ENABLED) !==
                    'true' || isTaskClosed
                }
                field={field}
                setValue={(value) => handleChange(field.slug, value)}
              />
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div
        className={`tw-w-full tw-p-5 tw-px-8 tw-border tw-border-solid !tw-border-x-0 !tw-border-b-0 tw-border-t-grey-200 tw-shadow-2xl tw-rounded-b-lg tw-flex tw-items-center ${
          isTaskClosed ? 'tw-justify-between' : 'tw-justify-end'
        }`}
      >
        {isTaskClosed ? (
          <span className='tw-flex tw-gap-2 tw-items-center tw-text-zinc-700'>
            <Info size={15} />
            <span className='tw-text-[1.4rem] tw-font-medium tw-whitespace-nowrap tw-leading-none'>
              Use global category search to update this category.
            </span>
          </span>
        ) : null}

        {/* {selectedEntityType === ENTITY_TYPE.PRODUCT ? (
          <Button
            type='link'
            className={'tw-mr-2 !tw-font-medium'}
            onClick={showParentCategoryDetails}
            title={'See corresponding category config'}
          />
        ) : null} */}

        <Button
          onClick={isTaskClosed ? getChangeLogs : handlePreview}
          loading={previewLoading}
          title={isTaskClosed ? 'See Change Log' : 'Preview Changes'}
        />
      </div>
    </div>
  );
};

export default ConfigUpdateForm;
